import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useChildTokens } from '@/components/child';
import type { RoutineStep } from '@/utils/routine';

/**
 * RoutineStepBricks — rutinens steg som legobitar i överkant av rutinkortet.
 *
 * En bit per steg. Avklarade bitar är fyllda, den man står på är markerad, de som
 * återstår är tomma. Bitarna säger alltså HUR MÅNGA steg som är kvar — inte vilka.
 *
 * Varför bilder och inte text: raden ersätter "steg 2 av 5" (routine.stepCounter), som
 * förutsätter att brukaren läser siffror. Alla barn gör inte det, och de behöver ändå
 * kunna se hur långt det är kvar. Bitar går att räkna med fingret, och en rad som fylls
 * visar rörelse framåt utan ett enda ord.
 *
 * Detta bryter medvetet mot "en sak i taget", som annars gäller i rutinvyn: steget
 * självt och dess timer är fortfarande sidans huvudsak, och bitarna säger ingenting om
 * VAD de återstående stegen innehåller.
 *
 * ...om inte handledaren har sagt att de ska göra det. `labels` slår på stegens namn
 * i bitarna för brukare som har nytta av dem (users.routine_step_labels, V010). Då blir
 * bitarna bredare och raden bryts i flera rader i stället för att krympa till oläsliga
 * klossar — men den scrollas aldrig bort, för då försvinner hela poängen.
 */

type RoutineStepBricksProps = {
  steps: RoutineStep[];
  /** Index för steget brukaren står på. Lika med steps.length = allt är klart. */
  currentIndex: number;
  /** Visa stegens namn i bitarna. Handledarens val för brukaren. */
  labels?: boolean;
};

const RoutineStepBricks = ({ steps, currentIndex, labels = false }: RoutineStepBricksProps) => {
  const { t } = useTranslation();
  const tokens = useChildTokens();

  if (steps.length === 0) return null;

  const done = Math.min(currentIndex, steps.length);

  return (
    <Box
      role="img"
      aria-label={t('routine.bricksAria', { done, total: steps.length }) as string}
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: 0.75,
        width: '100%',
      }}
    >
      {steps.map((step, index) => {
        const isDone = index < done;
        const isCurrent = index === done;
        return (
          <Box
            key={step.subtaskId}
            aria-hidden
            sx={{
              position: 'relative',
              // Utan namn: lika breda bitar som delar raden mellan sig, med en undre
              // gräns så att de inte krymper till prickar vid många steg.
              flex: labels ? '0 1 auto' : '1 1 0',
              minWidth: labels ? 0 : 18,
              maxWidth: labels ? '100%' : 56,
              height: labels ? 'auto' : 26,
              px: labels ? 1.25 : 0,
              py: labels ? 0.75 : 0,
              // Knopparna sitter i ovankanten som på en riktig legobit.
              mt: 0.75,
              borderRadius: '6px',
              bgcolor: isDone ? 'warning.main' : isCurrent ? tokens.amberLight : tokens.mutedLight,
              border: '2px solid',
              borderColor: isDone || isCurrent ? 'warning.main' : tokens.border,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s ease',
              '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
              '&::before, &::after': {
                content: '""',
                position: 'absolute',
                top: -5,
                width: 6,
                height: 5,
                borderRadius: '2px 2px 0 0',
                bgcolor: 'inherit',
                border: '2px solid',
                borderColor: 'inherit',
                borderBottom: 'none',
              },
              '&::before': { left: '22%' },
              '&::after': { right: '22%' },
            }}
          >
            {labels && (
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  lineHeight: 1.2,
                  color: isDone ? 'warning.contrastText' : tokens.text,
                  textDecoration: isDone ? 'line-through' : 'none',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 140,
                }}
              >
                {step.subtaskText}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default RoutineStepBricks;
